import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Save } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface ClientMessageTemplate {
  id: string;
  name: string;
  template_key: string;
  category: string;
  subject_template: string;
  html_template: string; 
  text_template: string;
  variables: any;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function ClientTemplateEditor() {
  const [templates, setTemplates] = useState<ClientMessageTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState<ClientMessageTemplate | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    template_key: '',
    category: 'client_notification',
    subject_template: '',
    html_template: '',
    text_template: '',
    is_active: true
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('client_message_templates')
        .select('*')
        .order('name');

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error fetching client templates:', error);
      toast.error('Failed to fetch client templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const templateData = {
        ...formData,
        variables: []
      };

      let error;
      if (editingTemplate) {
        ({ error } = await supabase
          .from('client_message_templates')
          .update(templateData)
          .eq('id', editingTemplate.id));
      } else {
        ({ error } = await supabase
          .from('client_message_templates')
          .insert([templateData]));
      }

      if (error) throw error;

      toast.success(`Client template ${editingTemplate ? 'updated' : 'created'} successfully`);
      fetchTemplates();
      resetForm();
    } catch (error) {
      console.error('Error saving client template:', error);
      toast.error(`Failed to ${editingTemplate ? 'update' : 'create'} client template`);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('client_message_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Client template deleted successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting client template:', error);
      toast.error('Failed to delete client template');
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const handleEdit = (template: ClientMessageTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      template_key: template.template_key,
      category: template.category,
      subject_template: template.subject_template,
      html_template: template.html_template,
      text_template: template.text_template,
      is_active: template.is_active
    });
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      template_key: '',
      category: 'client_notification',
      subject_template: '',
      html_template: '',
      text_template: '',
      is_active: true
    });
  };

  if (loading) {
    return <div className="flex justify-center p-8">Loading client templates...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Client Communication Templates</h2>
          <p className="text-muted-foreground">Manage email templates for client communication using their SMTP settings</p>
        </div>
        <Button onClick={resetForm} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          New Client Template
        </Button>
      </div>

      <div className="grid gap-4">
        {templates.map((template) => (
          <Card key={template.id}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  {template.name}
                  {!template.is_active && <Badge variant="destructive">Inactive</Badge>}
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                    Client
                  </Badge>
                </CardTitle>
                <CardDescription>
                  {template.template_key} • {template.category}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(template)}
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDeleteConfirmId(template.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>

      {/* Client Template Editor */}
      <Card>
        <CardHeader>
          <CardTitle>{editingTemplate ? 'Edit' : 'Create'} Client Email Template</CardTitle>
          <CardDescription>
            Configure email templates for client communication. These will use the client's Business Portal SMTP configuration.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="client-template-name">Name</Label>
              <Input
                id="client-template-name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Client template name"
              />
            </div>
            <div>
              <Label htmlFor="client-template-key">Template Key</Label>
              <Input
                id="client-template-key"
                value={formData.template_key}
                onChange={(e) => setFormData(prev => ({ ...prev, template_key: e.target.value }))}
                placeholder="Template key (e.g., client_action_immediate)"
              />
            </div>
            <div>
              <Label htmlFor="client-template-category">Category</Label>
              <Input
                id="client-template-category"
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                placeholder="client_notification"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="client-subject-template">Subject Template</Label>
            <Input
              id="client-subject-template"
              value={formData.subject_template}
              onChange={(e) => setFormData(prev => ({ ...prev, subject_template: e.target.value }))}
              placeholder="Email subject with merge tags"
            />
          </div>
          
          <div>
            <Label htmlFor="client-html-template">HTML Template</Label>
            <Textarea
              id="client-html-template"
              value={formData.html_template}
              onChange={(e) => setFormData(prev => ({ ...prev, html_template: e.target.value }))}
              placeholder="HTML email content with merge tags"
              rows={8}
            />
          </div>
          
          <div>
            <Label htmlFor="client-text-template">Text Template</Label>
            <Textarea
              id="client-text-template"
              value={formData.text_template}
              onChange={(e) => setFormData(prev => ({ ...prev, text_template: e.target.value }))}
              placeholder="Plain text email content with merge tags"
              rows={6}
            />
          </div>
          
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="client-template-active"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
              />
              <Label htmlFor="client-template-active">Active</Label>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={resetForm}>Cancel</Button>
              <Button onClick={handleSave}>
                <Save className="h-4 w-4 mr-2" />
                Save Client Template
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      {deleteConfirmId && (
        <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Client Template</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this client template? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => handleDelete(deleteConfirmId)}>
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}